import React from 'react';
import { useParams, useNavigate, useOutletContext, useLocation } from 'react-router-dom';
import { useQuery } from 'react-query';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import { HttpClient } from 'apiclient/ApiClient';
import { IStudentStats, IProgressStats, CompleteReportData } from 'models/IStats';
import StudentPerformanceChart from 'ui/components/StudentPerformanceChart';
import StatTable, { StatColumn } from 'ui/components/StatTable';
import { calculateSkillCategoryStats, calculateTopSkills } from 'utils/statsCalculations';
import ReportHeader from 'ui/components/shared-components/ReportHeader';
import { IOutletContext } from 'ui/components/Dashboard';
import { useSession } from 'auth/AuthProvider';
import './TeacherStats.css';

interface TeacherStatsProps {
    inline?: boolean;
    reportId?: string;
    onBack?: () => void;
}


const TeacherStats: React.FC<TeacherStatsProps> = ({ 
    inline = false, 
    reportId: propReportId,
    onBack 
}) => {
    const { reportId: paramReportId } = useParams<{ reportId: string }>();
    const navigate = useNavigate();
    const location = useLocation();
    const { setBackButtonProps } = useOutletContext<IOutletContext>();
    const session = useSession();
    const isManager = session.role === 'manager';
    const httpClient = HttpClient();
    
    // Use prop reportId if inline, otherwise use URL param
    const reportId = inline ? propReportId : paramReportId;
    
    // Set up back button when component mounts (only for non-inline mode)
    React.useEffect(() => {
        if (!inline) {
            const fromOrganization = location.state?.fromOrganization;
            const fromWeeklyReport = location.state?.fromWeeklyReport;
            const backType = fromOrganization ? 'school-report' : 'all-reports';
            
            setBackButtonProps({
                onBack: () => {
                    if (onBack) {
                        onBack();
                    } else if (fromOrganization && location.state?.orgReportId) {
                        navigate(`/organization_stat/${location.state.orgReportId}`);
                    } else if (fromWeeklyReport && location.state?.classId) {
                        // For managers, go to organization reports; for teachers, go to class reports
                        if (isManager) {
                            navigate('/organization-reports');
                        } else {
                            navigate(`/reports/${location.state.classId}`);
                        }
                    } else {
                        navigate(-1);
                    }
                },
                backType
            });
            
            // Clear back button when component unmounts
            return () => {
                setBackButtonProps({});
            };
        }
    }, [inline, navigate, onBack, location.state, setBackButtonProps, isManager]);
    

    // Combined query for complete report data
    const reportQuery = useQuery(
        [inline ? 'report_inline' : 'report', reportId],
        async () => {
            try {
                const response = await httpClient.get(`reports/${reportId}`);
                return response;
            } catch (error) {
                console.warn('Failed to fetch report data');
                throw error;
            }
        },
        { enabled: !!reportId }
    );

    const reportData = (reportQuery.data as any)?.data || (reportQuery.data as unknown as CompleteReportData);
    const studentsData = reportData?.students_data || [];
    const progressData = reportData?.progress_data || [];
    const isEmpty = reportData?.is_empty || false;
    const emptyReason = reportData?.empty_reason;

    if (reportQuery.isLoading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight={inline ? "200px" : "400px"}>
                <CircularProgress size={inline ? 30 : undefined} />
            </Box>
        );
    }

    if (reportQuery.error) {
        return (
            <div style={{ 
                color: '#dc2626', 
                fontSize: '14px', 
                padding: inline ? '10px 0' : '20px 0',
                textAlign: 'left' as const
            }}>
                {inline 
                    ? "Error loading class statistics data." 
                    : "Error loading statistics data. Please try again later."
                }
            </div>
        );
    }

    const students: IStudentStats[] = studentsData || [];
    const progress: IProgressStats[] = progressData || [];

    // Helper function to render empty state messages for different sections
    const renderEmptyState = (section: 'overview' | 'performance' | 'skills') => {
        if (!isEmpty && students.length === 0) {
            return (
                <div style={{ 
                    color: '#111827', 
                    fontSize: '16px', 
                    padding: inline ? '10px 0' : '20px 0',
                    textAlign: 'left' as const
                }}>
                    {inline 
                        ? "No data available for this class report." 
                        : "No data available for this report."
                    }
                </div>
            );
        }
        
        if (isEmpty) {
            // Determine message based on empty reason
            let message: string;
            
            if (emptyReason === "No students found in class") {
                message = "You haven't added students yet";
            } else if (emptyReason === "No data found for the specified date range") {
                message = section === 'skills' ? "No data for this period" : "No data for this period";
            } else {
                // Fallback messages
                message = section === 'skills' ? "No data for this period" : "You haven't added students yet";
            }
            
            return (
                <div style={{ 
                    color: '#111827', 
                    fontSize: '16px', 
                    padding: inline ? '10px 0' : '20px 0',
                    textAlign: 'left' as const
                }}>
                    {message}
                </div>
            );
        }
        
        return null;
    };

    // Get metadata from report instead of first student
    const className = reportData?.class_name || 'Unknown Class';
    const teacherName = reportData?.teacher || 'Unknown Teacher';

    // Calculate overview metrics
    const totalTasksSolved = progress.reduce((sum, p) => sum + p.tasks_solved, 0);
    const totalTimeSpent = progress.reduce((sum, p) => sum + p.time_spent, 0);
    const uniqueStudents = new Set(progress.map(p => p.student_name)).size;
    const timePerStudent = uniqueStudents > 0 ? totalTimeSpent / uniqueStudents : 0;
    const averageAccuracy = progress.length > 0 
        ? progress.reduce((sum, p) => sum + p.accuracy_rate, 0) / progress.length 
        : 0;

    // Calculate table data
    const skillCategoryStats = calculateSkillCategoryStats(progress);
    const numeracySkills = calculateTopSkills(progress, 'Numeracy', 5);
    const geometrySkills = calculateTopSkills(progress, 'Geometry', 5);
    const algorithmSkills = calculateTopSkills(progress, 'Algorithm', 5);
    const logicSkills = calculateTopSkills(progress, 'Logic', 5);

    // Table column definitions
    const skillCategoryColumns: StatColumn[] = [
        { key: 'skill_category', title: 'Skill Category', type: 'text' },
        { key: 'skills_practiced', title: '# of Skills Practiced', type: 'number' },
        { key: 'tasks_solved', title: '# of Tasks Solved', type: 'number' },
        { key: 'accuracy_rate', title: 'Accuracy Rate', type: 'accuracy' }
    ];

    const topSkillColumns: StatColumn[] = [
        { key: 'skill', title: 'Skill', type: 'text' },
        { key: 'students_count', title: '# of Students', type: 'number' },
        { key: 'tasks_solved', title: '# of Tasks Solved', type: 'number' },
        { key: 'accuracy_rate', title: 'Accuracy Rate', type: 'accuracy' }
    ];

    // Get inline styles for sections
    const getSectionStyle = (baseStyle = {}) => {
        if (inline) {
            return { ...baseStyle, padding: '30px 0' };
        }
        return baseStyle;
    };

    const getContainerStyle = () => {
        if (inline) {
            return { backgroundColor: 'white', padding: '20px 0', marginTop: '20px' };
        }
        return {};
    };

    return (
        <div className={inline ? '' : 'teacher-stats-page'} style={getContainerStyle()}>
            {/* Header - only show if not inline */}
            {!inline && (
                <ReportHeader 
                    type="class"
                    className={className}
                    teacherName={teacherName}
                    startDate={reportData?.start_date}
                    endDate={reportData?.end_date}
                />
            )}

            {/* Content */}
            <div className={inline ? '' : 'content'}>

                {/* Overview Section */}
                <div className="section" style={getSectionStyle()}>
                    <h2 className="section-title">{inline ? 'Overview' : '1. Overview'}</h2>
                    {renderEmptyState('overview') || (
                        <div className="overview-grid">
                            {[
                                { label: 'Total Tasks Solved', value: totalTasksSolved.toString() },
                                { label: 'Total Time Spent (min)', value: totalTimeSpent.toFixed(1) },
                                { label: 'Time per Student (min)', value: timePerStudent.toFixed(2) },
                                { label: 'Average Accuracy Rate', value: averageAccuracy.toFixed(2) },
                                { label: 'Number of Students', value: uniqueStudents.toString() }
                            ].map((metric, index, array) => (
                                <div 
                                    key={metric.label}
                                    className="metric-row"
                                    style={{
                                        borderBottom: index < array.length - 1 ? '1px solid #e5e7eb' : 'none'
                                    }}
                                >
                                    <span className="metric-label">{metric.label}</span>
                                    <span className="metric-value">{metric.value}</span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Student Performance Section */}
                <div className={`section ${inline ? '' : 'performance'}`} style={getSectionStyle()}>
                    <h2 className="section-title">
                        {inline ? 'Student performance' : '2. Student performance'}
                    </h2>
                    {renderEmptyState('performance') || (
                        <>
                            <div className="chart-container">
                                <StudentPerformanceChart students={students} />
                            </div>
                            <div className="description">
                                <strong>ThinkRank</strong> is a dynamic scoring system that reflects a student's skill proficiency. It is designed to measure progress, mastery, and learning patterns.
                                <br /><br />
                                The score combines three aspects of a student’s activity during the period:<br />
                                &bull; <b>Engagement</b> – how many tasks the student completes<br />
                                &bull; <b>Accuracy</b> – how correctly the student solves problems<br />
                                &bull; <b>Challenge level</b> – how complex the tasks they attempt are<br />
                                The score ranges from <b>1 to 100</b>, with higher values indicating stronger performance and engagement with more challenging tasks.
                            </div>
                        </>
                    )}
                </div>

                {/* Skill Performance Breakdown */}
                <div className="table-section" style={getSectionStyle()}>
                    <h2 className="section-title">{inline ? 'Skill Performance Breakdown' : '3. Skill Performance Breakdown'}</h2>
                    {renderEmptyState('skills') || (
                        <StatTable 
                            columns={skillCategoryColumns} 
                            data={skillCategoryStats} 
                        />
                    )}
                </div>

                {/* Top Skills Tables */}
                {numeracySkills.length > 0 && (
                    <div className="table-section" style={getSectionStyle()}>
                        <h2 className="section-title">Top {numeracySkills.length} Numeracy Skills</h2>
                        <StatTable 
                            columns={topSkillColumns} 
                            data={numeracySkills} 
                        />
                    </div>
                )}

                {geometrySkills.length > 0 && (
                    <div className="table-section" style={getSectionStyle()}>
                        <h2 className="section-title">Top {geometrySkills.length} Geometry & Spatial Thinking Skills</h2>
                        <StatTable 
                            columns={topSkillColumns} 
                            data={geometrySkills} 
                        />
                    </div>
                )}

                {algorithmSkills.length > 0 && (
                    <div className="table-section" style={getSectionStyle()}>
                        <h2 className="section-title">Top {algorithmSkills.length} Algorithmic Thinking Skills</h2>
                        <StatTable 
                            columns={topSkillColumns} 
                            data={algorithmSkills} 
                        />
                    </div>
                )}

                {logicSkills.length > 0 && (
                    <div className="table-section" style={getSectionStyle()}>
                        <h2 className="section-title">Top {logicSkills.length} Logic & Patterning Skills</h2>
                        <StatTable 
                            columns={topSkillColumns} 
                            data={logicSkills} 
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

export default TeacherStats;